import { useContext, useMemo, useState } from "react";
import { useStyles } from "./RCAIssues.styles";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { MainTable } from "components/MainTable";
import { Divider } from "@mui/material";
import useRCAIssuesColumns from "./useRCAIssuesColumns";
import RCAIssuesFilters from "./components/RCAIssuesFilters";
import { useRCAMainStyles } from "../RCA.styles";
import { CORPORATE_BACKOFFICE_PATHS } from "constants/constants";
import { useNavigate } from "react-router-dom";
import { IssueEntity } from "entities/RCA/IssueEntity/IssueEntity";
import useRolesPermissions from "hooks/useRolesPermissions";
import RCACreateEditIssuesModal from "../components/RCACreateEditIssuesModal";
import { useTableOperations } from "components/MainTable/hooks/useTableOperations";
import { RCAIssueFiltersContext } from "contexts/RCAIssueFiltersContext/RCAIssueFiltersContext";
import { RCAIssueFiltersContextProvider } from "contexts/RCAIssueFiltersContext/RCAIssueFiltersContextProvider";

const RCAIssuesContent = () => {
    const mainClasses = useRCAMainStyles();
    const classes = useStyles();
    const navigate = useNavigate();
    const { handleRequestSort, handleChangeRowsPerPage, handleChangePage, order, orderBy, page, rowsPerPage } =
        useTableOperations();
    const { filters } = useContext(RCAIssueFiltersContext);

    const queryTableOperationFilters = useMemo(
        () => ({
            order,
            orderBy,
            limit: rowsPerPage,
            offset: page * rowsPerPage,
        }),
        [order, orderBy, rowsPerPage, page],
    );

    const { issues, loading, apolloQueryError, refetch, totalCount } = IssueEntity.GetIssues(
        queryTableOperationFilters,
        filters,
    );

    const tableColumns = useRCAIssuesColumns(refetch) as unknown as IMainTableColumn[];
    const [isCreateIssueModalOpen, setIsCreateIssueModalOpen] = useState(false);
    const { RCA_PERMISSIONS } = useRolesPermissions();

    const parsedIssuesWithExpandedData = useMemo(() => {
        return issues.map((issue) => ({
            ...issue,
            expandedData: issue.action_plans ?? [],
        }));
    }, [issues]);

    return (
        <div className={mainClasses.mainWrapper}>
            <RCAIssuesFilters
                disableMillExecutiveFilter={!RCA_PERMISSIONS?.issues.executiveMillsFilter}
                onClickAddNewIssue={() => setIsCreateIssueModalOpen(true)}
            />
            <div className={mainClasses.contentWrapper}>
                <div className={`${mainClasses.contentCard} ${classes.cardWrapper}`}>
                    <Divider />
                    <MainTable
                        maxHeight={false}
                        onRowClick={(row: { issue_id?: number }) => {
                            navigate(
                                `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.ROOT}/${row.issue_id}`,
                                {
                                    state: {
                                        from: `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.ROOT}`,
                                    },
                                },
                            );
                        }}
                        onExpandedRowClick={(row: { action_plan_id?: number }) => {
                            navigate(
                                `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_ACTION_PLANS.ROOT}/${row.action_plan_id}`,
                                {
                                    state: {
                                        from: `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.ROOT}`,
                                    },
                                },
                            );
                        }}
                        customStyle={`${mainClasses.contentTable}`}
                        paperElevation={0}
                        paperCustomStyle={mainClasses.contentTablePaper}
                        columns={tableColumns}
                        customStyleTypography={{ width: "100%" }}
                        tableCellCustomStyle={mainClasses.contentTableCell}
                        tableExpandedRowCustomStyle={mainClasses.contentTableExpandedRow}
                        rows={!loading && !apolloQueryError ? parsedIssuesWithExpandedData : []}
                        loading={loading}
                        tableOperationsValues={{ order, orderBy, page, rowsPerPage }}
                        handleRequestSortParent={handleRequestSort}
                        handleChangeRowsPerPageParent={handleChangeRowsPerPage}
                        handleChangePageParent={handleChangePage}
                        pagination
                        count={totalCount}
                        querySorted={!!orderBy}
                    />
                </div>
            </div>
            <RCACreateEditIssuesModal
                open={isCreateIssueModalOpen}
                onClose={() => setIsCreateIssueModalOpen(false)}
                onAddIssue={async () => {
                    await refetch();
                }}
            />
        </div>
    );
};

const RCAIssues = () => {
    return (
        <RCAIssueFiltersContextProvider>
            <RCAIssuesContent />
        </RCAIssueFiltersContextProvider>
    );
};

export default RCAIssues;
